import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    FetchInventoryActivityLogsInput,
    InventoryActivityLogEntry,
    InventoryActivityLogResponse,
    InventoryImportHistoryEntry,
    InventoryImportHistoryResponse,
} from '../../types/inventory';

const getInventoryActivityLogs = async (
    sessionId: string,
    input: FetchInventoryActivityLogsInput,
): Promise<InventoryActivityLogResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/activity-logs`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

function mapImportLog(log: InventoryActivityLogEntry): InventoryImportHistoryEntry | null {
    const meta = log.metadata ?? {};
    const jobId = meta.jobId;
    if (!jobId) return null;
    const createdCount = Number(meta.createdCount ?? 0);
    const failedRaw = meta.failedCount;
    const failedCount =
        failedRaw != null && failedRaw !== '' ? Number(failedRaw) : undefined;
    return {
        jobId,
        createdAt: log.timestamp,
        createdCount: Number.isFinite(createdCount) ? createdCount : 0,
        failedCount: failedCount != null && Number.isFinite(failedCount) ? failedCount : undefined,
        filename: meta.filename || undefined,
    };
}

export const listImportHistory = async (
    sessionId: string,
    limit = 50,
): Promise<InventoryImportHistoryResponse> => {
    const data = await getInventoryActivityLogs(sessionId, {
        all: true,
        page: 1,
        limit,
        eventTypes: ['inventory-imported'],
    });
    const imports = data.logs
        .map(mapImportLog)
        .filter((e): e is InventoryImportHistoryEntry => e != null);
    return { imports };
};

export const useInventoryImportHistory = (enabled = true, limit = 50) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: [
            '/inventory/activity-logs',
            'inventory-imported',
            sessionContext?.organizationId ?? '',
            limit,
        ],
        queryFn: () => listImportHistory(sessionContext?.sessionId ?? '', limit),
        enabled: enabled && !!sessionContext?.sessionId,
    });
};
